// src/components/tickets/detail/ReassignDialog.tsx
//
// The reassign dialog (Story 4.4 AC 7). Offers the org's ACTIVE team members
// (name + position; via 1.4's teamService.listTeam() through useTeamMembers —
// reuse, never a second users query), EXCLUDING the current assignee but
// INCLUDING the acting user (reassign-to-self is the FR-36 escape hatch). Primary
// "Reassign to {name}". On a business failure the mapped copy shows inline and
// the picked user is preserved.
//
// Overlay follows the repo idiom (fixed inset-0, role=dialog, Esc closes, focus
// returns to the trigger — handled by the caller). One modal level.

import { useEffect, useRef, useState } from 'react'
import { useTeamMembers } from '../../../hooks/useTeamMembers'
import { personName } from '../../../utils/ticketDisplay'

interface ReassignDialogProps {
  // The ticket's current assignee — excluded from the picker.
  currentAssigneeId: string
  reassigning: boolean
  // Mapped business-failure copy to show inline (null = none); selection survives.
  error: string | null
  onReassign: (targetUserId: string) => void
  onCancel: () => void
}

export function ReassignDialog({
  currentAssigneeId,
  reassigning,
  error,
  onReassign,
  onCancel,
}: ReassignDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const { members, loading, error: loadError } = useTeamMembers()

  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  // Active console users, minus the current assignee (self stays — the escape
  // hatch). Server enforces active/role; this filtering is cosmetic.
  const candidates = members.filter(
    m => m.status === 'active' && m.id !== currentAssigneeId
  )
  const selectedMember = candidates.find(m => m.id === selected) ?? null
  const primaryLabel = selectedMember
    ? `Reassign to ${personName(selectedMember)}`
    : 'Reassign'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-title"
        aria-describedby={error ? 'reassign-error' : undefined}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
      >
        <h3
          id="reassign-title"
          className="mb-3 text-lg font-semibold text-gray-900"
        >
          Reassign this ticket
        </h3>

        {loading ? (
          <p className="mb-4 text-sm text-gray-500">Loading team…</p>
        ) : loadError ? (
          <p className="mb-4 text-sm text-red-700">{loadError}</p>
        ) : candidates.length === 0 ? (
          <p className="mb-4 text-sm text-gray-500">
            No other active team members to reassign to.
          </p>
        ) : (
          <ul className="mb-4 max-h-64 space-y-1 overflow-y-auto">
            {candidates.map(m => {
              const isSel = m.id === selected
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(m.id)}
                    disabled={reassigning}
                    aria-pressed={isSel}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                      isSel
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium text-gray-900">
                      {personName(m)}
                    </span>
                    {m.position ? (
                      <span className="text-gray-500"> · {m.position}</span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {error ? (
          <div
            id="reassign-error"
            role="alert"
            className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={reassigning}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => selected && onReassign(selected)}
            disabled={reassigning || !selected}
            className="btn-outline"
          >
            {reassigning ? 'Reassigning…' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
